// Primitive 8 — Toast.
// Région `aria-live` : un message de confirmation ou d'erreur doit être
// annoncé aux lecteurs d'écran, pas seulement affiché.

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, tone = 'ok', durationMs = 5000) => {
      const id = crypto.randomUUID();
      setToasts((list) => [...list, { id, message, tone }]);
      // Une erreur ne disparaît pas toute seule : l'utilisateur doit
      // pouvoir la lire et la fermer quand il l'a comprise.
      if (tone !== 'error') setTimeout(() => dismiss(id), durationMs);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      push,
      dismiss,
      success: (m) => push(m, 'ok'),
      error: (m) => push(m, 'error'),
      warn: (m) => push(m, 'warn'),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.tone}`}>
            <span className="toast__text">{t.message}</span>
            <button
              type="button"
              className="btn btn--ghost btn--sm btn--icon"
              aria-label="Fermer ce message"
              onClick={() => dismiss(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast doit être utilisé dans un ToastProvider.');
  return ctx;
}

export default ToastProvider;
